import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { InstallPWAButton } from '@/components/InstallPWAButton';
import { InstallBanner } from '@/components/InstallBanner';
import {
  GraduationCap,
  Flame,
  Zap,
  Target,
  Brain,
  BookOpen,
  Atom,
  FlaskConical,
  Timer,
  BarChart3,
  Sparkles,
  ChevronRight,
  Star,
  ArrowRight,
  Trophy,
  Shield,
  Rocket,
  Gift,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.5, ease: 'easeOut' },
};

export default function LandingPage() {
  const nav = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Ambient grid + radial background */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          backgroundImage:
            "radial-gradient(1200px circle at 15% -10%, rgba(220,38,38,0.28), transparent 55%), radial-gradient(900px circle at 90% 10%, rgba(239,68,68,0.16), transparent 55%), radial-gradient(700px circle at 50% 110%, rgba(239,68,68,0.14), transparent 60%)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-0 opacity-[0.14]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)",
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 70%)',
        }}
      />

      {/* First-time install banner — above sticky nav */}
      <InstallBanner />

      {/* Nav */}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-black/70 backdrop-blur">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-brand">
            <div className="grid h-9 w-9 place-items-center rounded-lg bg-red-600 text-white shadow-[0_0_28px_rgba(239,68,68,0.55)]">
              <GraduationCap className="h-5 w-5" />
            </div>
            <div className="leading-tight">
              <div className="font-display text-base font-semibold">StudyTrack AI</div>
              <div className="text-[11px] text-white/50">NCERT · JEE · NEET</div>
            </div>
          </Link>
          <nav className="hidden items-center gap-6 md:flex">
            <a href="#features" className="text-sm text-white/70 hover:text-white">
              Features
            </a>
            <a href="#tracks" className="text-sm text-white/70 hover:text-white">
              Tracks
            </a>
            <a href="#pricing" className="text-sm text-white/70 hover:text-white">
              Pricing
            </a>
            <a href="#how" className="text-sm text-white/70 hover:text-white">
              How it works
            </a>
            <a href="#faq" className="text-sm text-white/70 hover:text-white">
              FAQ
            </a>
          </nav>
          <div className="flex items-center gap-2">
            <InstallPWAButton
              variant="outline"
              size="sm"
              className="border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300"
            />
            {user ? (
              <Button
                onClick={() => nav('/dashboard')}
                className="bg-red-600 text-white hover:bg-red-500"
                data-testid="landing-open-dashboard"
              >
                Open dashboard
              </Button>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => nav('/login')}
                  className="text-white hover:bg-white/10"
                  data-testid="landing-nav-login"
                >
                  Log in
                </Button>
                <Button
                  onClick={() => nav('/register')}
                  className="bg-red-600 text-white shadow-[0_0_28px_rgba(239,68,68,0.35)] hover:bg-red-500"
                  data-testid="landing-nav-register"
                >
                  Start free
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10">
        <div className="mx-auto max-w-6xl px-4 pb-14 pt-16 sm:px-6 sm:pt-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mx-auto max-w-3xl text-center"
          >
            <div className="mx-auto mb-5 inline-flex items-center gap-2 rounded-full border border-red-500/40 bg-red-500/10 px-3 py-1 text-xs font-medium text-red-300">
              <Flame className="h-3.5 w-3.5" />
              PYQ-style AI mocks · Fresh every attempt
            </div>
            <h1 className="font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-6xl md:text-7xl">
              Crack{' '}
              <span className="relative inline-block">
                <span className="bg-gradient-to-b from-red-400 via-red-500 to-red-700 bg-clip-text text-transparent">
                  Boards. JEE. NEET.
                </span>
                <span
                  aria-hidden
                  className="absolute -inset-x-4 -bottom-2 h-2 rounded-full bg-red-500/40 blur-md"
                />
              </span>
              <br />
              one chapter at a time.
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-base text-white/70 sm:text-lg">
              35 fresh AI-generated PYQ-style questions per set. Live timer. Inline diagrams.
              An AI report card that tells you exactly where you're leaking marks.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Button
                size="lg"
                onClick={() => nav(user ? '/dashboard' : '/register')}
                className="h-12 gap-2 bg-red-600 px-6 text-base font-semibold text-white shadow-[0_0_36px_rgba(239,68,68,0.55)] hover:bg-red-500"
                data-testid="landing-hero-cta"
              >
                <Rocket className="h-5 w-5" />
                Start a free mock now
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => nav('/login')}
                className="h-12 gap-2 border-white/20 bg-white/5 px-6 text-base text-white hover:bg-white/10"
                data-testid="landing-hero-login"
              >
                I already have an account
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs text-white/60">
              <span className="inline-flex items-center gap-1.5">
                <Star className="h-3.5 w-3.5 fill-red-500 text-red-500" />
                Built for Class 11 & 12
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Shield className="h-3.5 w-3.5 text-red-400" />
                100% NCERT-aligned
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Zap className="h-3.5 w-3.5 text-red-400" />
                Free to start · No card
              </span>
            </div>
          </motion.div>

          {/* Floating stat bar */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="relative mx-auto mt-14 grid max-w-4xl gap-3 sm:grid-cols-4"
          >
            <Stat kpi="35" label="Questions / set + PYQ-style PDF" />
            <Stat kpi="3" label="Sets per chapter" />
            <Stat kpi="4" label="Subjects covered" />
            <Stat kpi="Stopwatch" label="Track your own pace" />
          </motion.div>
        </div>
      </section>

      {/* Tracks */}
      <section id="tracks" className="relative z-10 border-t border-white/10 bg-black/60">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24">
          <motion.div {...fadeUp} className="mb-10 text-center">
            <div className="mx-auto mb-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-medium text-white/70">
              <Target className="h-3.5 w-3.5 text-red-400" />
              Three tracks · One relentless engine
            </div>
            <h2 className="font-display text-3xl font-semibold sm:text-4xl">Choose your battlefield</h2>
            <p className="mx-auto mt-3 max-w-xl text-white/60">
              Each track is fine-tuned for its exam pattern — question style, difficulty mix, and analysis.
            </p>
          </motion.div>
          <div className="grid gap-5 md:grid-cols-3">
            <TrackCard
              title="Boards"
              subtitle="Theory + Practicals"
              icon={BookOpen}
              points={['NCERT-first questions', 'Practicals viva Q&A', 'Derivations checklist']}
              onClick={() => nav(user ? '/boards' : '/register')}
              testId="landing-track-boards"
            />
            <TrackCard
              title="JEE"
              subtitle="Main + Advanced"
              icon={Atom}
              points={['Multi-concept problems', 'PYQ + coaching-module style', 'Numerical + application heavy']}
              onClick={() => nav(user ? '/pick?track=JEE' : '/register')}
              featured
              testId="landing-track-jee"
            />
            <TrackCard
              title="NEET (UG)"
              subtitle="Physics · Chem · Bio"
              icon={FlaskConical}
              points={['NCERT-line accuracy', 'Assertion-reason + facts', 'Diagram-based Bio drills']}
              onClick={() => nav(user ? '/pick?track=NEET' : '/register')}
              testId="landing-track-neet"
            />
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="relative z-10 border-t border-white/10">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24">
          <motion.div {...fadeUp} className="mb-10 max-w-2xl">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-red-500/40 bg-red-500/10 px-3 py-1 text-xs font-medium text-red-300">
              <Sparkles className="h-3.5 w-3.5" />
              What makes it hit different
            </div>
            <h2 className="font-display text-3xl font-semibold sm:text-4xl">
              A mock engine that thinks like an exam setter.
            </h2>
            <p className="mt-3 text-white/60">
              Not another question bank. Every attempt is freshly generated — so you can't memorise it, you have to master it.
            </p>
          </motion.div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <Feature
              icon={Brain}
              title="AI-crafted PYQ-style MCQs"
              desc="Fresh 35 questions every set. Balanced difficulty: 30% easy, 50% medium, 20% hard. Style mimics real exam papers."
            />
            <Feature
              icon={Timer}
              title="Live stopwatch"
              desc="Counts up from the moment you start. See exactly how long each attempt takes and beat your own time on retakes."
            />
            <Feature
              icon={Sparkles}
              title="Inline SVG diagrams"
              desc="Physics circuits, biology cells, geometry figures — rendered live inside the question, sanitised for safety."
            />
            <Feature
              icon={BarChart3}
              title="AI report card"
              desc="Score, grade, weak topics with priority, mistake patterns, next 3 study actions, and a motivational note."
            />
            <Feature
              icon={Target}
              title="35 × 3 sets per chapter (JEE / NEET chapterwise PDF)"
              desc="Three distinct sets per NCERT chapter — Sets 1 & 2 are AI mock tests, Set 3 is a curated chapterwise PDF for JEE / NEET."
            />
            <Feature
              icon={Trophy}
              title="Full history + retake"
              desc="Track every attempt across chapters and subjects. See how you're improving over time."
            />
            <Feature
              icon={Gift}
              title="Refer & Earn"
              desc="Share your code — when a friend buys a plan, you BOTH get 1 bonus chapter unlocked. Usable on any section."
            />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="relative z-10 border-t border-white/10 bg-gradient-to-b from-black to-black/60">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24">
          <motion.div {...fadeUp} className="mb-10 text-center">
            <h2 className="font-display text-3xl font-semibold sm:text-4xl">From zero to report card in 4 steps</h2>
          </motion.div>
          <div className="grid gap-4 md:grid-cols-4">
            {[
              { n: '01', title: 'Pick a chapter', desc: 'Choose Boards, JEE, or NEET → class → subject → NCERT chapter → Set 1, 2, or 3.' },
              { n: '02', title: 'AI generates 35 Qs', desc: 'A fresh set is crafted for you in ~40 seconds. Diagrams included where they matter.' },
              { n: '03', title: 'Attempt like the real deal', desc: 'Timed, palette-navigated, mark-for-review. Auto-submits when time expires.' },
              { n: '04', title: 'Get your AI report card', desc: 'Instant weak-topic map + prioritised next actions. Explanations on every question.' },
            ].map((s, i) => (
              <motion.div
                key={s.n}
                {...fadeUp}
                transition={{ duration: 0.5, delay: 0.05 * i }}
                className="group relative overflow-hidden rounded-xl border border-white/10 bg-white/[0.03] p-5"
              >
                <div className="absolute -right-3 -top-3 font-display text-6xl font-bold text-white/[0.05]">
                  {s.n}
                </div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-red-400">Step {s.n}</div>
                <div className="font-display text-lg font-semibold">{s.title}</div>
                <div className="mt-1 text-sm text-white/60">{s.desc}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Social proof strip */}
      <section className="relative z-10 border-y border-white/10 bg-red-600/[0.08]">
        <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
          <div className="grid gap-6 md:grid-cols-3">
            <Quote
              text="I finally know which topics are killing my marks. The AI report is scary accurate."
              author="Ananya · Class 12 · JEE aspirant"
            />
            <Quote
              text="The timer plus palette feels exactly like the real NEET screen. Great practice."
              author="Rohit · Class 12 · NEET aspirant"
            />
            <Quote
              text="Physics diagrams inside questions? That's a game-changer for Boards prep."
              author="Meera · Class 11 · Boards"
            />
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="relative z-10 border-t border-white/10">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24">
          <motion.div {...fadeUp} className="mb-10 max-w-2xl">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-red-500/40 bg-red-500/10 px-3 py-1 text-xs font-medium text-red-300">
              <Flame className="h-3.5 w-3.5" />
              1 chapter free per section · No credit card
            </div>
            <h2 className="font-display text-3xl font-semibold sm:text-4xl">Simple, honest pricing.</h2>
            <p className="mt-3 text-white/60">
              Pick a single section for <strong className="text-white">₹999/year</strong> — or unlock every track (Boards + JEE + NEET) with All-Access for <strong className="text-white">₹1,899/year</strong>.
            </p>
          </motion.div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[
              { name: 'Boards · Theory', price: 999, features: ['NCERT chapters', 'Derivations checklist'] },
              { name: 'Boards · Practicals', price: 999, features: ['Viva Q&A + tips', 'Every practical chapter'] },
              { name: 'JEE (Main + Adv.)', price: 999, features: ['Full JEE syllabus', 'Derivations mastery'] },
              { name: 'NEET (UG)', price: 999, features: ['Full NEET syllabus', 'Diagram Bio drills'] },
            ].map((p) => (
              <motion.div
                key={p.name}
                {...fadeUp}
                className="rounded-xl border border-white/10 bg-white/[0.03] p-5"
              >
                <div className="font-display text-base font-semibold">{p.name}</div>
                <div className="mt-2 font-display text-3xl font-bold">
                  ₹{p.price}
                  <span className="text-sm font-normal text-white/50"> / year</span>
                </div>
                <ul className="mt-3 space-y-1 text-sm text-white/70">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-center gap-2">
                      <span className="inline-block h-1.5 w-1.5 rounded-full bg-red-500" />
                      {f}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
          <motion.div
            {...fadeUp}
            className="relative mt-6 overflow-hidden rounded-2xl border border-red-500/40 bg-gradient-to-br from-red-600/20 via-black to-black p-6 sm:p-8"
          >
            <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
              <div>
                <div className="mb-1 inline-flex rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
                  Best Value
                </div>
                <div className="font-display text-2xl font-semibold sm:text-3xl">
                  All Access · Boards + JEE + NEET
                </div>
                <div className="mt-1 text-sm text-white/70">
                  Save ₹2,097 vs individual plans. Every chapter, every set, every viva unlocked.
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="font-display text-4xl font-bold">
                  ₹1,899<span className="text-base font-normal text-white/50"> / yr</span>
                </div>
                <Button
                  onClick={() => nav(user ? '/pricing' : '/register')}
                  className="h-11 gap-2 bg-red-600 px-5 font-semibold text-white shadow-[0_0_28px_rgba(239,68,68,0.45)] hover:bg-red-500"
                  data-testid="landing-see-plans-cta"
                >
                  <Rocket className="h-4 w-4" />
                  Get All Access
                </Button>
              </div>
            </div>
          </motion.div>
          <div className="mt-4 text-center text-xs text-white/50">
            Pay via GPay / any UPI · Scan QR · Enter UTR → plan activates instantly.
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="relative z-10 border-b border-white/10">
        <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 sm:py-24">
          <motion.div {...fadeUp} className="mb-8">
            <h2 className="font-display text-3xl font-semibold sm:text-4xl">Common questions</h2>
          </motion.div>
          <div className="space-y-3">
            <Faq q="Are these real previous year questions?">
              Questions are AI-generated in the style of PYQs and standard coaching modules (Allen / Aakash /
              Resonance style). This means fresh, exam-like practice you can't memorise. If you need verbatim PYQ
              banks, we can add them next.
            </Faq>
            <Faq q="Is it really free?">
              Yes — sign up, take mocks, get AI report cards. You'll only ever need to pay if we launch premium
              features later.
            </Faq>
            <Faq q="Which classes and subjects are covered?">
              Class 11 & 12. Physics, Chemistry, Mathematics, Biology — full NCERT chapter list.
            </Faq>
            <Faq q="How long does a mock take?">
              35 questions per set. There's no forced time limit — a live stopwatch shows exactly how long you took, so you can beat your own pace on the next attempt.
            </Faq>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative z-10">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24">
          <motion.div
            {...fadeUp}
            className="relative overflow-hidden rounded-3xl border border-red-500/30 bg-gradient-to-br from-red-600/20 via-black to-black p-8 sm:p-14"
          >
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0"
              style={{
                backgroundImage:
                  'radial-gradient(600px circle at 90% 20%, rgba(239,68,68,0.28), transparent 55%)',
              }}
            />
            <div className="relative max-w-2xl">
              <h3 className="font-display text-3xl font-semibold leading-tight sm:text-5xl">
                Your next 30 minutes decides your next mock score.
              </h3>
              <p className="mt-3 text-white/70">
                Skip the doom-scroll. Take one AI-crafted chapter mock and see exactly where you stand.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button
                  size="lg"
                  onClick={() => nav(user ? '/dashboard' : '/register')}
                  className="h-12 gap-2 bg-red-600 px-6 text-base font-semibold text-white shadow-[0_0_36px_rgba(239,68,68,0.55)] hover:bg-red-500"
                  data-testid="landing-final-cta"
                >
                  <Flame className="h-5 w-5" />
                  {user ? 'Open your dashboard' : 'Start free — take a mock'}
                </Button>
                {!user && (
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => nav('/login')}
                    className="h-12 border-white/20 bg-white/5 px-6 text-white hover:bg-white/10"
                  >
                    Log in
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/10 bg-black">
        <div className="mx-auto flex w-full max-w-6xl flex-col items-center justify-between gap-3 px-4 py-8 text-xs text-white/50 sm:flex-row sm:px-6">
          <div className="flex items-center gap-2">
            <div className="grid h-6 w-6 place-items-center rounded-md bg-red-600 text-white">
              <GraduationCap className="h-3.5 w-3.5" />
            </div>
            <span>StudyTrack AI · Built for serious students</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="#features" className="hover:text-white">Features</a>
            <a href="#tracks" className="hover:text-white">Tracks</a>
            <a href="#faq" className="hover:text-white">FAQ</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Stat({ kpi, label }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 text-center backdrop-blur">
      <div className="font-display text-2xl font-bold text-white sm:text-3xl">
        <span className="bg-gradient-to-b from-red-300 to-red-500 bg-clip-text text-transparent">{kpi}</span>
      </div>
      <div className="mt-1 text-xs uppercase tracking-wider text-white/60">{label}</div>
    </div>
  );
}

function TrackCard({ title, subtitle, icon: Icon, points, onClick, featured, testId }) {
  return (
    <motion.button
      {...fadeUp}
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`group relative overflow-hidden rounded-2xl border p-6 text-left transition-all ${
        featured
          ? 'border-red-500/60 bg-gradient-to-b from-red-600/15 to-black shadow-[0_0_60px_-20px_rgba(239,68,68,0.45)]'
          : 'border-white/10 bg-white/[0.03] hover:border-red-500/30'
      }`}
    >
      {featured && (
        <div className="absolute right-4 top-4 rounded-full border border-red-400/40 bg-red-500/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-red-200">
          Most popular
        </div>
      )}
      <div className="mb-4 flex items-center gap-3">
        <div
          className={`grid h-11 w-11 place-items-center rounded-lg ${
            featured
              ? 'bg-red-600 text-white shadow-[0_0_24px_rgba(239,68,68,0.5)]'
              : 'bg-white/10 text-white'
          }`}
        >
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="font-display text-xl font-semibold">{title}</div>
          <div className="text-xs text-white/60">{subtitle}</div>
        </div>
      </div>
      <ul className="mb-5 space-y-1.5 text-sm text-white/70">
        {points.map((p) => (
          <li key={p} className="flex items-start gap-2">
            <span className="mt-2 inline-block h-1.5 w-1.5 flex-none rounded-full bg-red-500" />
            {p}
          </li>
        ))}
      </ul>
      <div
        className={`inline-flex items-center gap-1.5 text-sm font-semibold ${
          featured ? 'text-red-300' : 'text-white/80 group-hover:text-red-300'
        }`}
      >
        Explore chapters
        <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
      </div>
    </motion.button>
  );
}

function Feature({ icon: Icon, title, desc }) {
  return (
    <motion.div
      {...fadeUp}
      className="group relative overflow-hidden rounded-xl border border-white/10 bg-white/[0.03] p-5 transition-colors hover:border-red-500/30"
    >
      <div className="mb-3 inline-grid h-10 w-10 place-items-center rounded-lg bg-red-600/15 text-red-400">
        <Icon className="h-5 w-5" />
      </div>
      <div className="font-display text-lg font-semibold">{title}</div>
      <div className="mt-1 text-sm text-white/60">{desc}</div>
    </motion.div>
  );
}

function Quote({ text, author }) {
  return (
    <motion.div {...fadeUp} className="rounded-xl border border-white/10 bg-black/40 p-5">
      <div className="mb-2 text-red-400">
        <Star className="inline h-4 w-4 fill-red-500 text-red-500" />
        <Star className="inline h-4 w-4 fill-red-500 text-red-500" />
        <Star className="inline h-4 w-4 fill-red-500 text-red-500" />
        <Star className="inline h-4 w-4 fill-red-500 text-red-500" />
        <Star className="inline h-4 w-4 fill-red-500 text-red-500" />
      </div>
      <p className="text-sm text-white/80">&ldquo;{text}&rdquo;</p>
      <div className="mt-3 text-xs uppercase tracking-wider text-white/50">{author}</div>
    </motion.div>
  );
}

function Faq({ q, children }) {
  return (
    <details className="group rounded-xl border border-white/10 bg-white/[0.03] p-4 open:border-red-500/40">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3">
        <span className="font-display text-base font-semibold">{q}</span>
        <span className="grid h-6 w-6 place-items-center rounded-full border border-white/20 text-white/70 transition-transform group-open:rotate-45">
          +
        </span>
      </summary>
      <p className="mt-3 text-sm text-white/70">{children}</p>
    </details>
  );
}
